import HomeBannerLoader from "./HomeBannerLoader";
import HomeBannerScene from "./HomeBannerScene";

// Part de la progression de sortie au bout de laquelle le dégradé recouvre déjà
// tout l'écran (le reste de la course sert de marge avant le masquage du fond).
const OUTRO_VEIL_END = 0.85;

// Moteur 3D autonome (chargement des assets, boucle de rendu, parallax souris,
// resize) — ne dépend pas de GSAP/ScrollTrigger : la progression de scroll lui
// est transmise depuis le bundle principal via setScrollProgress().
export default class HomeBanner3D {
	constructor(root) {
		this.root = root;
		const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		const themeLink = (window.skArgs && window.skArgs.themeLink) || "";

		this.loader = new HomeBannerLoader(root);
		this.scene = new HomeBannerScene({
			canvas1: root.querySelector(".banner-home-canvas-1"),
			canvas2: root.querySelector(".banner-home-canvas-2"),
			annotCanvas: root.querySelector(".banner-home-annotations"),
			fadeEl: root.querySelector(".banner-home-fade"),
			whiteEl: root.querySelector(".banner-home-white"),
			screen1El: root.querySelector(".banner-home-screen-1"),
			basePath: `${themeLink}/models`,
			dracoDecoderPath: `${themeLink}/lib/draco/`,
			reducedMotion,
		});

		this._outroT = 0;

		this._onMouseMove = (e) => this.scene.setMouse(e.clientX, e.clientY);
		window.addEventListener("mousemove", this._onMouseMove);

		this._onResize = () => this.scene.resize();
		window.addEventListener("resize", this._onResize);

		// Le rendu est suspendu une fois le conteneur entièrement scrollé (bottom <= 0) :
		// sans ça, les deux renderers WebGL + le calcul des annotations tournaient à 60fps
		// pour rien pendant tout le reste de la session, même bien après le hero.
		this.visible = true;
		this._intersectionObserver = new IntersectionObserver(([entry]) => {
			this.visible = entry.isIntersecting;
			this.root.classList.toggle("banner-home--out-of-view", !this.visible);
		});
		this._intersectionObserver.observe(this.root);

		this._tick = () => {
			if (this.visible) this.scene.render();
			this._raf = requestAnimationFrame(this._tick);
		};
		this._raf = null;

		this._init();
	}

	async _init() {
		this.loader.startRipple();

		await this.scene.load();

		this.loader.hide();
		this.scene.startIntro();
		this._tick();

		window.dispatchEvent(new CustomEvent("homeBanner3dReady", { detail: { instance: this } }));

		// Préchargement de la scène 2 pendant l'intro (5,2 s), en tranches calées sur
		// le rAF : sa génération de nuages de points et son upload GPU sortent ainsi
		// du scroll, où ils bloquaient le main thread au premier passage entre les
		// deux scènes. La boucle de rendu doit tourner pour que les tranches avancent,
		// d'où l'appel après _tick().
		this.scene.preloadScene2();
	}

	setScrollProgress(progress) {
		this.scene.setScrollProgress(progress);
	}

	// Sortie de séquence (derniers 100vh de la bannière, après la fin du scrub) :
	// purement DOM/CSS, la scène 3D n'est pas concernée. Le fond fixe remonte
	// légèrement pendant qu'un dégradé de la couleur de fond du site le recouvre
	// par le bas — le voile atteint le plein recouvrement un peu avant que
	// l'IntersectionObserver ne masque le fond, pour éviter tout flash.
	setOutroProgress(progress) {
		const p = Math.max(0, Math.min(1, progress));
		if (p === this._outroT) return;
		this._outroT = p;
		this.root.style.setProperty("--home-outro-shift", p);
		this.root.style.setProperty("--home-outro-veil", Math.min(1, p / OUTRO_VEIL_END));
	}
}
